import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const ThemeContext = createContext();

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeContextProvider');
  }
  return context;
};

// Design tokens for consistent spacing and colors
const designTokens = {
  spacing: 8, // 8px base unit
  borderRadius: {
    small: 4,
    medium: 8,
    large: 12,
    xlarge: 16
  },
  shadows: {
    light: '0 2px 8px rgba(0,0,0,0.1)',
    medium: '0 4px 20px rgba(0,0,0,0.15)',
    heavy: '0 8px 30px rgba(0,0,0,0.2)'
  }
};

export const ThemeContextProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    const savedMode = localStorage.getItem('themeMode');
    return savedMode || 'light';
  });

  const toggleTheme = useCallback(() => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    localStorage.setItem('themeMode', newMode);
  }, [mode]);

  // Memoized theme creation to prevent unnecessary re-renders
  const theme = useMemo(() => createTheme({
    spacing: designTokens.spacing,
    shape: {
      borderRadius: designTokens.borderRadius.medium
    },
    palette: {
      mode,
      primary: {
        50: '#e3f2fd',
        100: '#bbdefb',
        200: '#90caf9',
        300: '#64b5f6',
        400: '#42a5f5',
        500: mode === 'light' ? '#1976d2' : '#90caf9',
        600: '#1e88e5',
        700: '#1976d2',
        800: '#1565c0',
        900: '#0d47a1',
        main: mode === 'light' ? '#1976d2' : '#90caf9',
        light: mode === 'light' ? '#42a5f5' : '#e3f2fd',
        dark: mode === 'light' ? '#1565c0' : '#42a5f5',
      },
      secondary: {
        main: '#f50057',
        light: '#ff4081',
        dark: '#c51162',
      },
      background: {
        default: mode === 'light' ? '#f5f5f5' : '#121212',
        paper: mode === 'light' ? '#ffffff' : '#1e1e1e',
      },
      text: {
        primary: mode === 'light' ? '#000000' : '#ffffff',
        secondary: mode === 'light' ? '#666666' : '#b0b0b0',
      },
      grey: {
        50: '#fafafa',
        100: '#f5f5f5',
        200: '#eeeeee',
        300: '#e0e0e0',
        400: '#bdbdbd',
        500: '#9e9e9e',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: [
        '"Inter"',
        '"Roboto"', 
        '"Helvetica Neue"',
        'Arial', 
        'sans-serif'
      ].join(','),
      h1: { 
        fontSize: 'clamp(2rem, 5vw, 3.5rem)',
        fontWeight: 700, 
        lineHeight: 1.1,
        letterSpacing: '-0.02em'
      },
      h2: { 
        fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
        fontWeight: 700, 
        lineHeight: 1.2,
        letterSpacing: '-0.01em'
      },
      h3: { 
        fontSize: 'clamp(1.5rem, 3vw, 2rem)',
        fontWeight: 700, 
        lineHeight: 1.3 
      },
      h4: {
        fontSize: 'clamp(1.25rem, 2.5vw, 1.75rem)',
        fontWeight: 600,
        lineHeight: 1.4
      },
      h5: {
        fontSize: 'clamp(1.125rem, 2vw, 1.5rem)',
        fontWeight: 600,
        lineHeight: 1.4
      },
      h6: {
        fontSize: 'clamp(1rem, 1.5vw, 1.25rem)',
        fontWeight: 600,
        lineHeight: 1.4
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.5
      }
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: designTokens.borderRadius.medium,
            textTransform: 'none',
            fontWeight: 600,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: mode === 'light' ? '#1976d2' : '#90caf9',
              outlineOffset: '2px'
            }
          },
          contained: {
            boxShadow: designTokens.shadows.light,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: designTokens.shadows.medium
            },
            '&:active': {
              transform: 'translateY(0)'
            }
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: designTokens.borderRadius.medium,
              '&:focus-within': {
                boxShadow: `0 0 0 2px ${mode === 'light' ? 'rgba(25, 118, 210, 0.2)' : 'rgba(144, 202, 249, 0.2)'}`
              }
            }
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: designTokens.borderRadius.large,
            boxShadow: designTokens.shadows.light,
            '&:hover': {
              boxShadow: designTokens.shadows.medium
            }
          }
        }
      }
    }
  }), [mode]);

  useEffect(() => {
    localStorage.setItem('themeMode', mode);
    document.body.setAttribute('data-theme', mode);
  }, [mode]);

  const contextValue = useMemo(() => ({
    mode,
    toggleTheme,
    theme
  }), [mode, toggleTheme, theme]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
